import { useQuery } from "@tanstack/react-query"; // prettier-ignore
import spices from "../data/spices.json";
import { NUTRIENTS, APISchema, LocalSpice, APISpice, type Cache } from "../data/schema"; //prettier-ignore
import { RESOURCES } from "@/data/resources";

const API_KEY = import.meta.env.VITE_SPICES_API_KEY as string;
type USDAFoodItem = {
  fdcId: number;
  foodNutrients: Array<{
    nutrient: { id: number; unitName: string };
    amount: number;
  }>;
};

// Batch fetch
export const fetchBatchData = async (ids: number[]) => {
  const response = await fetch(
    `https://api.nal.usda.gov/fdc/v1/foods?&fdcIds=${ids.join(",")}&api_key=${API_KEY}`
  );
 if (!response.ok) {
  const body = await response.text();
  console.error("USDA error", response.status, body);
  throw new Error(`HTTP ${response.status}`);
}

  return response.json();
};

// Filter
export const filterNutrition = (usdaNutrients: USDAFoodItem["foodNutrients"]) => {
  // Create a Map for O(1) lookup of the API data
  const apiMap = new Map(usdaNutrients.map((n) => [n.nutrient.id, n]));

  // Map over YOUR source of truth (NUTRIENTS) to ensure the array length matches Schema
  return NUTRIENTS.map((definedNutrient) => {
    const found = apiMap.get(definedNutrient.id);

    // USDA is per 100g. We convert to per 1 gram for the Cache.
    const amountPer100g = found?.amount ?? 0;     // If not found in API, default to 0.
    const amountPerGram = amountPer100g / 100;

    return {
      id: definedNutrient.id,
      name: definedNutrient.name, // Use your internal name key
      amount: Number(amountPerGram.toFixed(4)), // Keep precision for small values
      unit: definedNutrient.dailyValue.unit, // Force your preferred unit
    };
  });
};

/** Fetch
  fetch all data in the beginning as batch and only once
  merge batch with initial data, that needs to load immedtiately, because fetched data belongs with localdata, but localdata needs to be present immediately
  provide a o(1) lookup of each spice, to make it easy and accessible to set new data, check/look it up and get it
  fetch a spice immediately if the batch fetch isnt finished when its needed
*/

export function useCache() {

  const query = useQuery<Cache>({
    queryKey: [RESOURCES.nutrition.key],
    queryFn: async () => {
      //console.log("Fetching spices..."); // logs every time fetch runs
      const fetchedArray = await fetchBatchData(
        Object.keys(spices).map(Number)
      );

      // FIX 1: Strongly type fetchedMap using the USDAFoodItem type
      const fetchedMap: Record<number, USDAFoodItem> = {};
      for (const f of fetchedArray) {
        fetchedMap[f.fdcId] = f;
      }

      // FIX 2: Strongly type merged object to hold either the final API structure or the local one
      const merged: Record<number, APISpice | LocalSpice> = {};

      // Define the set of valid keys for the spices JSON object
      type SpiceIdKey = keyof typeof spices;

      for (const id in spices) {
        // FIX 3: Assert the type of the key 'id' to fix the index signature error
        const spiceIdKey = id as SpiceIdKey;
        const numericId = Number(id); // runtime number

        // FIX 4: Assert the value is LocalSpice to use its properties safely
        const local = spices[spiceIdKey] as LocalSpice;
        local.portions.gram = 1; // fallback

        const fetchedItem = fetchedMap[numericId];
        if (!fetchedItem) {
          merged[numericId] = local; // fallback
          continue;
        }

        const filteredNutrition = filterNutrition(fetchedItem.foodNutrients);

        try {
          merged[numericId] = APISchema.parse({
            ...local,
            nutrition: filteredNutrition,
          });
        } catch (err) {
          console.error(`Validation failed for spice ${id}`, err);
          merged[numericId] = local;
        }
      }

      return merged;
    },

     staleTime: Infinity,
  });

  return query
}
